import { cn } from '@optools/components/ui/utils';
import { FeedTypeCode } from '@optools/services/operator-gateway/btc/types';
import {
  PipelineBroadcastOverride,
  PipelineBroadcastSource,
  Region,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { z } from 'zod';

import { CreateBroadcastOverrideDialog } from './create-broadcast-override-dialog';
import { DeleteBroadcastOverrideDialog } from './delete-broadcast-override-dialog';
import { UpdateBroadcastOverrideDialog } from './update-broadcast-override-dialog';

export interface DecoderStatusProps {
  callSign: string;
  decoder: PipelineBroadcastSource | undefined | null;
  feedTypeCode: FeedTypeCode;
  gamePk: number;
  override: PipelineBroadcastOverride | undefined | null;
  region: Region;
}

enum DecoderStatusEnum {
  Error,
  Ok,
  Override,
}

const urlSchema = z.url();
const multicastAddressSchema = z.url();

const getStatus = (
  override: PipelineBroadcastOverride | undefined | null,
  decoder: PipelineBroadcastSource | undefined | null,
  region: Region,
) => {
  if (
    override &&
    (multicastAddressSchema.safeParse(override.multicastAddress).success ||
      !!override.fronthaulDecoderInputLabel) &&
    region === override.region
  ) {
    return DecoderStatusEnum.Override;
  }

  if (
    decoder &&
    (multicastAddressSchema.safeParse(decoder.multicastAddress).success ||
      decoder.decoderInputLabel) &&
    urlSchema.safeParse(decoder?.host).success
  ) {
    return DecoderStatusEnum.Ok;
  }
  return DecoderStatusEnum.Error;
};

const getStatusMessage = (status: DecoderStatusEnum) => {
  switch (status) {
    case DecoderStatusEnum.Ok:
      return 'Healthy';
    case DecoderStatusEnum.Override:
      return 'Override';
    case DecoderStatusEnum.Error:
      return 'Error';
  }
};

const getInputLabel = (
  override: PipelineBroadcastOverride | undefined | null,
  decoder: PipelineBroadcastSource | undefined | null,
  status: DecoderStatusEnum,
) => {
  if (status === DecoderStatusEnum.Override) {
    return override?.fronthaulDecoderInputLabel ? override.fronthaulDecoderInputLabel : 'None';
  } else {
    return decoder?.decoderInputLabel ? decoder.decoderInputLabel : 'None';
  }
};

const getMulticastAddress = (
  override: PipelineBroadcastOverride | undefined | null,
  decoder: PipelineBroadcastSource | undefined | null,
  status: DecoderStatusEnum,
) => {
  if (status === DecoderStatusEnum.Override) {
    return override?.multicastAddress ? override.multicastAddress : 'None';
  } else {
    return decoder?.multicastAddress ? decoder.multicastAddress : 'None';
  }
};

export function DecoderStatus({
  callSign,
  decoder,
  feedTypeCode,
  gamePk,
  override,
  region,
}: DecoderStatusProps) {
  const status = getStatus(override, decoder, region);
  const message = getStatusMessage(status);
  const inputLabel = getInputLabel(override, decoder, status);
  const multicastAddress = getMulticastAddress(override, decoder, status);

  return (
    <div className="flex w-[16rem] flex-col gap-2 overflow-hidden">
      <div className="flex items-center gap-1">
        <div
          className={cn(
            'flex h-6 w-20 items-center justify-center rounded-lg text-center font-bold',
            status === DecoderStatusEnum.Ok && 'bg-green-500',
            status === DecoderStatusEnum.Error && 'bg-red-500',
            status === DecoderStatusEnum.Override && 'bg-yellow-500',
          )}
          data-testid="decoder-status-badge">
          <p>{message}</p>
        </div>
        <div className="flex items-end justify-end gap-2">
          {status === DecoderStatusEnum.Override && override ? (
            <>
              <UpdateBroadcastOverrideDialog
                feedTypeCode={feedTypeCode}
                gamePk={gamePk}
                override={override}
              />
              <DeleteBroadcastOverrideDialog
                feedTypeCode={feedTypeCode}
                gamePk={gamePk}
                override={override}
              />
            </>
          ) : (
            <CreateBroadcastOverrideDialog
              callSign={callSign}
              feedTypeCode={feedTypeCode}
              gamePk={gamePk}
              region={region}
            />
          )}
        </div>
      </div>
      <div className="flex flex-col text-xs">
        <dl className="flex gap-1 whitespace-nowrap" data-testid="input-label-container">
          <dt className="opacity-60">Input Label:</dt>
          <dd className="truncate">{inputLabel}</dd>
        </dl>
        <dl className="flex gap-1 whitespace-nowrap" data-testid="host-container">
          <dt className="opacity-60">Host:</dt>
          <dd className="truncate">{decoder?.host ? decoder.host : 'None'}</dd>
        </dl>
        <dl className="flex gap-1 whitespace-nowrap" data-testid="multicast-address-container">
          <dt className="opacity-60">Multicast Address:</dt>
          <dd className="truncate">{multicastAddress}</dd>
        </dl>
      </div>
    </div>
  );
}
